import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';

import { TooltipStyled } from '@experimental-components/TooltipStyled';
import IconHelp from '@experimental-components/IconsComponents/Help';

import ReportsTable from 'components/ReportsTable';
import DisplayMessage from 'components/DisplayMessageV2';
import PlaceholdersTable from 'components/PlaceholdersTable';
import TableCustomContainer from 'components/GeneralTable/TableContainer';

import { Value } from '@experimental-components/dataFormatting';

import { Skeleton } from '@mui/material';

import ChipStatus from '@experimental-components/ChipStatus';

import { statusCashierTypesVariants, statusTipTypesVariants } from 'utils/reports/statusTypes';

import { ordersStatusLabels, ordersStatusVariants } from 'utils/reports/orderStates';

import OrderTypeItem from 'components/OrderTypeItem';

import ChanelItem from 'components/ChanelItem';

import PageError from 'components/PageError';

import { useState } from 'react';

import { ContentProps, RowsProps } from './types';

interface HeadersProps {
  name: string;
  sorting: number;
  tooltip?: string | null;
  sortable?: boolean;
  sortName?: string;
}

interface Props {
  isLoading?: boolean;
  isLoadingNextPage?: boolean;
  isFetched?: boolean;
  headers: HeadersProps[];
  rows: RowsProps[];
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  defaultColumnsLength?: number;
  alignment?: ContentProps['alignment'];
  emptyMessage?: string;
  title?: string;
  canHoverRow?: boolean;
  withRowId?: boolean;
  onRowClick?: (rowUuid: string) => void;
  withChipStatus?: boolean;
  chipIndex?: number;
  withChipOrderItem?: boolean;
  chipOrderItem?: number;
  withChannelItem?: boolean;
  channelIndex?: number;
  variantType?: string;
  withPadding?: boolean;
  isError?: boolean;
  refetch?: () => void;
  onSorting?: (data: { sortBy?: string }) => void;
}

interface ContentItemProps {
  content: ContentProps[];
  withChipStatus?: boolean;
  chipIndex?: number;
  withChipOrderItem?: boolean;
  chipOrderItem?: number;
  withChannelItem?: boolean;
  channelIndex?: number;
  variantType?: string;
}

function Content({
  content,
  withChipStatus,
  chipIndex,
  variantType = 'tip',
  withChipOrderItem,
  chipOrderItem,
  withChannelItem,
  channelIndex,
}: ContentItemProps) {
  const { t } = useTranslation('reports');

  const getVariant = (type: string, value: string) => {
    switch (type) {
      case 'tip':
        return statusTipTypesVariants[value as keyof typeof statusTipTypesVariants];
      case 'cashier':
        return statusCashierTypesVariants[value as keyof typeof statusCashierTypesVariants];
      case 'order':
        return ordersStatusVariants[value as keyof typeof ordersStatusVariants];
      default:
        return statusTipTypesVariants[value as keyof typeof statusTipTypesVariants];
    }
  };

  const renderComponent = (itemContent: ContentProps) => {
    switch (itemContent?.component) {
      case 'chip':
        return (
          <ChipStatus
            label={
              variantType === 'order'
                ? `${t(ordersStatusLabels[itemContent?.value as keyof typeof ordersStatusLabels])}`
                : itemContent?.value
            }
            size="small"
            variant={getVariant(variantType, itemContent?.value)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {content.map((itemContent, index) => {
        return (
          <ReportsTable.Cell align={itemContent?.alignment} component="th" scope="row">
            {itemContent?.type === 'text' ? (
              itemContent?.value
            ) : (
              <>
                {itemContent?.component
                  ? renderComponent(itemContent)
                  : chipIndex &&
                    index === chipIndex &&
                    withChipStatus && (
                      <ChipStatus
                        label={
                          variantType === 'order'
                            ? `${t(ordersStatusLabels[itemContent?.value as keyof typeof ordersStatusLabels])}`
                            : itemContent?.value
                        }
                        size="small"
                        variant={getVariant(variantType, itemContent?.value)}
                      />
                    )}

                {index === channelIndex && withChannelItem && <ChanelItem value={itemContent?.value} />}
                {chipOrderItem && index === chipOrderItem && withChipOrderItem && (
                  <OrderTypeItem label={itemContent?.value} value={itemContent?.symbol || ''} />
                )}
                {index !== chipIndex &&
                  index !== chipOrderItem &&
                  index !== channelIndex &&
                  !itemContent?.component && (
                    <Value
                      symbol={itemContent.symbol}
                      symbolPosition={itemContent.symbolPosition}
                      value={itemContent.value}
                    />
                  )}
              </>
            )}
          </ReportsTable.Cell>
        );
      })}
    </>
  );
}

function RowsInContentTable({
  isLoading,
  isLoadingNextPage,
  headers,
  rows,
  hasNextPage,
  fetchNextPage,
  isFetched,
  defaultColumnsLength = 4,
  alignment = 'left',
  emptyMessage,
  title,
  canHoverRow = false,
  withRowId = false,
  onRowClick = () => {},
  withChipStatus = false,
  chipIndex,
  withChipOrderItem = false,
  chipOrderItem,
  withChannelItem = false,
  channelIndex,
  variantType,
  withPadding = false,
  isError = false,
  refetch,
  onSorting = () => {},
}: Props) {
  const { t } = useTranslation('reports');
  const [activeColumn, setActiveColumn] = useState<string>('');
  function handleOnIntersecting() {
    if (!isLoadingNextPage) fetchNextPage();
  }

  function handleRowClick(rowUuid: string) {
    onRowClick(rowUuid);
  }

  if (isLoading) {
    return (
      <ReportsTable>
        <ReportsTable.Head>
          <ReportsTable.Row>
            {Array.from(Array(defaultColumnsLength).keys()).map((item) => (
              <ReportsTable.HeadCell align="left" key={item}>
                <Box
                  alignItems="center"
                  display="flex"
                  justifyContent={alignment}
                  paddingX={withPadding ? '8px' : '0px'}
                >
                  <Skeleton height={30} variant="text" width={100} />
                </Box>
              </ReportsTable.HeadCell>
            ))}
          </ReportsTable.Row>
        </ReportsTable.Head>
        <ReportsTable.Body>
          <PlaceholdersTable placeholderCols={defaultColumnsLength} />
        </ReportsTable.Body>
      </ReportsTable>
    );
  }
  if (isError) {
    return (
      <Box m="6rem">
        <PageError
          id="CashRegisterListError"
          labelAction={t('common:buttons.retry')}
          message={t('reports:messages.reportSubtitleServerError')}
          onAction={refetch}
          title={t('reports:messages.reportTitleServerError')}
        />
      </Box>
    );
  }

  if (rows.length === 0 && !isLoading && isFetched) {
    return (
      <Box mt={24}>
        <DisplayMessage
          height="90%"
          id="DisplayMessageEmptyNoData"
          maxWidth={384}
          message={emptyMessage || t('reports:messages.noInformationTryWithOtherDate')}
          sizeIcon={120}
          sizeMessage={16}
          sizeTitle={18}
          title={title || t('reports:messages.noInformation')}
        />
      </Box>
    );
  }

  return (
    <TableCustomContainer hasNextPage={hasNextPage} onIntersecting={handleOnIntersecting} stickyHeader>
      <ReportsTable.Head>
        <ReportsTable.Row>
          {headers.map((head) => {
            return (
              <ReportsTable.HeadCell
                activeColumn={activeColumn === head.sortName}
                align="left"
                isSorting={head.sortable}
                key={head.name}
                onClick={() => {
                  setActiveColumn(head.sortName || '');
                  onSorting({ sortBy: head.sortName });
                }}
              >
                <Box
                  alignItems="center"
                  display="flex"
                  justifyContent={alignment}
                  paddingX={withPadding ? '8px' : '0px'}
                >
                  {head?.name}
                  {head?.tooltip && (
                    <TooltipStyled style={{ marginLeft: '1rem' }} title={head?.tooltip || ''}>
                      <IconHelp size={16} />
                    </TooltipStyled>
                  )}
                </Box>
              </ReportsTable.HeadCell>
            );
          })}
        </ReportsTable.Row>
      </ReportsTable.Head>

      <ReportsTable.Body>
        {rows?.map((row, index) => (
          <ReportsTable.Row
            hover={canHoverRow}
            id={withRowId ? `row-${index}:${row.uuid}` : undefined}
            key={row.uuid ?? index}
            onClick={() => {
              return handleRowClick(row.uuid);
            }}
          >
            <Content
              channelIndex={channelIndex}
              chipIndex={chipIndex}
              chipOrderItem={chipOrderItem}
              content={row.content}
              variantType={variantType}
              withChannelItem={withChannelItem}
              withChipOrderItem={withChipOrderItem}
              withChipStatus={withChipStatus}
            />
          </ReportsTable.Row>
        ))}

        {isLoadingNextPage && <PlaceholdersTable placeholderCols={defaultColumnsLength} />}
      </ReportsTable.Body>
    </TableCustomContainer>
  );
}

export default RowsInContentTable;
